// Phase H (Jul-2026) — Receivables Report Pack.
// Seven config-driven reports over the unified Receivables engine +
// shortcut to the existing Aging Outstanding report screen.
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, Card } from "@/components/ui-helpers";
import { BranchFilter } from "@/components/BranchFilter";
import { useAuth } from "@/contexts/AuthContext";
import { toast, Toaster } from "sonner";
import { ArrowRight, ChevronDown, ChevronLeft, ChevronRight, Download, Loader2, Play, Search } from "lucide-react";

const INR = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const inputCls = "border border-slate-300 px-2.5 py-2 text-xs bg-white focus:outline-none focus:border-orange-500";

const RETENTION_HEADS = ["Retention PO Terms", "IR Retention", "Safety Retention",
  "BG Retention", "GST Retention", "Other Retention"];
const MODES = ["Cash", "Bank", "UPI", "Cheque"];

const col = (k, h, fmt = "text", align) => ({ k, h, fmt, align: align || (fmt === "inr" || fmt === "num" ? "right" : "left") });

const REPORTS = [
  {
    key: "collection", label: "Collection Register", endpoint: "collection-register",
    filters: ["date_from", "date_to", "client_id", "branch_id", "project_type", "mode", "group_by", "q"],
    columns: [col("payment_date", "Date"), col("receipt_number", "Receipt No"),
      col("voucher_number", "Voucher No"), col("client_name", "Client"),
      col("project_name", "Project"), col("branch_name", "Branch"), col("mode", "Mode"),
      col("reference_no", "Reference"), col("amount", "Gross", "inr"),
      col("tds_deducted", "TDS", "inr"), col("net_amount", "Net Received", "inr"),
      col("has_attachment", "Attach."), col("created_by", "Entered By")],
    totals: (t) => [["Receipts", t.receipts], ["Gross", INR(t.amount)], ["TDS", INR(t.tds)], ["Net", INR(t.net)]],
  },
  {
    key: "outstanding", label: "Client Outstanding", endpoint: "client-outstanding",
    filters: ["client_id", "branch_id", "fy", "project_type"],
    rowsKey: "clients",
    columns: [col("client_name", "Client"), col("invoices", "Invoices", "num"),
      col("billed", "Billed", "inr"), col("received", "Received", "inr"),
      col("credit_notes", "CN", "inr"), col("debit_notes", "DN", "inr"),
      col("outstanding", "Outstanding", "inr"),
      col("ret_po", "PO Terms Ret.", "inr"), col("ret_ir", "IR Ret.", "inr"),
      col("ret_safety", "Safety Ret.", "inr"), col("ret_bg", "BG Ret.", "inr"),
      col("ret_gst", "GST Ret.", "inr"), col("ret_other", "Other Ret.", "inr"),
      col("pending_retention", "Net Retention", "inr"),
      col("net_collectible", "Net Collectible", "inr"), col("overdue_amount", "Overdue", "inr")],
    totals: (t) => [["Invoices", t.invoices], ["Outstanding", INR(t.outstanding)],
      ["Net Retention", INR(t.pending_retention)], ["Net Collectible", INR(t.net_collectible)],
      ["Overdue", INR(t.overdue_amount)]],
    drill: {
      rowsKey: "invoices", label: "Invoice Detail",
      columns: [col("invoice_number", "Invoice"), col("invoice_date", "Date"),
        col("client_name", "Client"), col("project_name", "Project"),
        col("net_payable", "Net Payable", "inr"), col("received", "Received", "inr"),
        col("outstanding", "Outstanding", "inr"),
        col("ret_po", "PO Terms Ret.", "inr"), col("ret_ir", "IR Ret.", "inr"),
        col("ret_safety", "Safety Ret.", "inr"), col("ret_bg", "BG Ret.", "inr"),
        col("ret_gst", "GST Ret.", "inr"), col("ret_other", "Other Ret.", "inr"),
        col("pending_retention", "Net Ret.", "inr"),
        col("net_collectible", "Net Collectible", "inr"),
        col("ses_date", "SES Date"), col("ses_status", "SES Status"),
        col("due_date", "Due Date"),
        col("due_basis", "Due Basis"), col("overdue_days", "Overdue Days", "num"),
        col("receivable_status", "Status")],
    },
  },
  {
    key: "retention", label: "Retention Register", endpoint: "retention-register",
    filters: ["client_id", "branch_id", "project_type", "head", "ret_status"],
    columns: [col("invoice_number", "Invoice"), col("client_name", "Client"),
      col("project_name", "Project"), col("branch_name", "Branch"), col("head", "Head"),
      col("retention_date", "Held On"), col("held_amount", "Held", "inr"),
      col("released_amount", "Released", "inr"), col("pending_amount", "Pending", "inr"),
      col("status", "Status"), col("days_pending", "Days Pending", "num"),
      col("request_status", "Request"), col("request_number", "Request No"),
      col("expected_release_date", "Expected Release")],
    totals: (t) => [["Rows", t.rows], ["Held", INR(t.held)], ["Released", INR(t.released)], ["Pending", INR(t.pending)]],
  },
  {
    key: "cndn", label: "CN / DN Register", endpoint: "cn-dn-register",
    filters: ["client_id", "branch_id", "fy", "project_type", "note_type"],
    expandable: "allocation_history",
    columns: [col("note_type", "Type"), col("note_number", "Note No"), col("note_date", "Date"),
      col("client_name", "Client"), col("reference_invoice", "Ref. Invoice"),
      col("total_amount", "Amount", "inr"), col("allocated_amount", "Allocated", "inr"),
      col("available_balance", "Available", "inr"), col("status", "Status"),
      col("application", "Application")],
    totals: (t) => [["Notes", t.notes], ["CN Total", INR(t.cn_total)],
      ["CN Available", INR(t.cn_available)], ["DN Total", INR(t.dn_total)]],
  },
  {
    key: "tds", label: "TDS Summary", endpoint: "tds-summary",
    filters: ["client_id", "branch_id", "fy", "project_type", "only_variance"],
    columns: [col("invoice_number", "Invoice"), col("invoice_date", "Date"),
      col("client_name", "Client"), col("project_name", "Project"),
      col("invoice_tds", "Invoice TDS", "inr"), col("suggested_tds", "Suggested TDS", "inr"),
      col("receipt_tds", "Receipt TDS", "inr"), col("variance", "Variance", "inr"),
      col("tds_mode", "Basis"), col("override_reasons", "Override Reasons")],
    totals: (t) => [["Invoices", t.invoices], ["Invoice TDS", INR(t.invoice_tds)],
      ["Receipt TDS", INR(t.receipt_tds)], ["Variance", INR(t.variance)]],
  },
  {
    key: "rvreg", label: "Receipt Voucher Register", endpoint: "receipt-voucher-register",
    filters: ["date_from", "date_to", "client_id", "branch_id", "rv_status", "mode"],
    columns: [col("voucher_number", "Voucher"), col("voucher_date", "Date"),
      col("client_name", "Client"), col("mode", "Mode"), col("allocations", "Allocs", "num"),
      col("total_amount", "Received", "inr"), col("tds_total", "TDS", "inr"),
      col("retention_held", "Ret. Held", "inr"), col("retention_released", "Ret. Released", "inr"),
      col("cn_allocated", "CN Adj.", "inr"), col("cess", "CESS", "inr"), col("ld", "LD", "inr"),
      col("round_off", "Round-off", "inr"), col("attachments", "Attach.", "num"),
      col("status", "Status"), col("reversal_reason", "Reversal Reason")],
    totals: (t) => [["Vouchers", t.vouchers], ["Active", t.active], ["Reversed", t.reversed],
      ["Received", INR(t.amount)], ["Retention Held", INR(t.retention_held)]],
  },
  {
    key: "followup", label: "Invoice Follow-up", endpoint: "invoice-followup",
    filters: ["client_id", "branch_id", "fy", "project_type", "queue_view", "ses_pending", "q"],
    columns: [col("invoice_number", "Invoice"), col("invoice_date", "Date"),
      col("client_name", "Client"), col("project_name", "Project"),
      col("outstanding", "Outstanding", "inr"), col("payment_status", "Payment"),
      col("receivable_status", "Invoice Status"), col("current_remark", "Current Remark"),
      col("status_updated_at", "Last Updated"), col("status_updated_by", "Updated By"),
      col("ses_date", "SES Date"), col("ses_status", "SES Status"),
      col("due_date", "Due Date"), col("days_overdue", "Days Overdue", "num"),
      col("retention_status", "Retention")],
    totals: (t) => [["Invoices", t.invoices], ["Outstanding", INR(t.outstanding)],
      ["Pending Retention", INR(t.pending_retention)]],
  },
];

export default function ReceivablesReports() {
  const { hasPerm } = useAuth();
  const canExport = hasPerm("receivables_reports", "export");
  const [active, setActive] = useState(REPORTS[0]);
  const [filters, setFilters] = useState({});
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [masters, setMasters] = useState({ clients: [], fys: [], workTypes: [] });
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    const arr = (r) => (Array.isArray(r.data) ? r.data : r.data?.items || r.data?.rows || []);
    Promise.all([api.get("/clients"), api.get("/financial-years")])
      .then(([c, f]) => setMasters((m) => ({ ...m, clients: arr(c), fys: arr(f) })))
      .catch(() => {});
    // Project Type filter — Work Types master from the Project library.
    api.get("/work-types")
      .then((r) => setMasters((m) => ({ ...m, workTypes: (r.data || []).map((w) => w.name).filter(Boolean) })))
      .catch(() => {});
  }, []);

  const params = useMemo(() => {
    const p = {};
    if (filters.date_from) p.date_from = filters.date_from;
    if (filters.date_to) p.date_to = filters.date_to;
    if (filters.client_id) p.client_id = filters.client_id;
    if (filters.branch_id) p.branch_id = filters.branch_id;
    if (filters.project_type) p.project_type = filters.project_type;
    if (filters.mode) p.mode = filters.mode;
    if (filters.group_by) p.group_by = filters.group_by;
    if (filters.q) p.q = filters.q;
    if (filters.fy) p.fy = filters.fy;
    if (filters.head) p.head = filters.head;
    if (filters.ret_status) p.status = filters.ret_status;
    if (filters.note_type) p.note_type = filters.note_type;
    if (filters.only_variance) p.only_variance = true;
    if (filters.rv_status) p.status = filters.rv_status;
    if (filters.queue_view) p.view = filters.queue_view;
    if (filters.ses_pending) p.ses_pending = true;
    p.limit = 5000;
    return p;
  }, [filters]);

  async function run(rep = active, f = params) {
    setLoading(true);
    setExpanded(null);
    try {
      const r = await api.get(`/receivables-reports/${rep.endpoint}`, { params: f });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load report");
      setData(null);
    } finally { setLoading(false); }
  }
  useEffect(() => { run(active); /* eslint-disable-next-line */ }, [active.key]);

  async function exportXlsx() {
    setExporting(true);
    try {
      const r = await api.get(`/receivables-reports/${active.endpoint}`,
        { params: { ...params, fmt: "xlsx" }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${active.endpoint}_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Export failed");
    } finally { setExporting(false); }
  }

  const rows = data ? (data[active.rowsKey || "rows"] || []) : [];
  const drillRows = active.drill && data ? (data[active.drill.rowsKey] || []) : [];
  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const has = (k) => active.filters.includes(k);

  return (
    <div className="p-6 max-w-[1500px]" data-testid="receivables-reports-page">
      <Toaster position="top-right" richColors />
      <PageHeader eyebrow="Documents & Reports" title="Receivables Reports"
        subtitle="Phase H report pack — every figure derives from the same unified Receivables engine that drives the Main Dashboard, Debtor/HR Dashboard and Aging Report."
        actions={canExport && (
          <button onClick={exportXlsx} disabled={exporting || loading} data-testid="rr-export-btn"
            className="inline-flex items-center gap-1.5 bg-slate-900 hover:bg-slate-700 text-white px-3 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors disabled:opacity-50">
            {exporting ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />} Export Excel
          </button>
        )} />

      <div className="flex flex-wrap gap-1 mb-4" data-testid="rr-tabs">
        {REPORTS.map((r) => (
          <button key={r.key} onClick={() => { setActive(r); }} data-testid={`rr-tab-${r.key}`}
            className={`px-3 py-2 text-xs font-bold uppercase tracking-wider border transition-colors ${
              active.key === r.key ? "bg-[#F97316] text-white border-[#F97316]"
                : "bg-white text-slate-600 border-slate-300 hover:border-orange-400"}`}>
            {r.label}
          </button>
        ))}
        <a href="/reports/aging-outstanding" data-testid="rr-aging-link"
          className="px-3 py-2 text-xs font-bold uppercase tracking-wider border border-dashed border-slate-400 text-slate-500 hover:text-orange-600 hover:border-orange-400 inline-flex items-center gap-1">
          Aging Outstanding <ArrowRight size={11} />
        </a>
      </div>

      <Card className="mb-4">
        <div className="p-3 flex flex-wrap items-end gap-2">
          {has("date_from") && (
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">From
              <input type="date" data-testid="rr-filter-from" className={`${inputCls} block mt-1`}
                value={filters.date_from || ""} onChange={(e) => set("date_from", e.target.value)} />
            </label>)}
          {has("date_to") && (
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">To
              <input type="date" data-testid="rr-filter-to" className={`${inputCls} block mt-1`}
                value={filters.date_to || ""} onChange={(e) => set("date_to", e.target.value)} />
            </label>)}
          {has("client_id") && (
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Client
              <select data-testid="rr-filter-client" className={`${inputCls} block mt-1 max-w-[220px]`}
                value={filters.client_id || ""} onChange={(e) => set("client_id", e.target.value)}>
                <option value="">All clients</option>
                {masters.clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </label>)}
          {has("branch_id") && (
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Branch
              <div className="mt-1"><BranchFilter value={filters.branch_id || ""} onChange={(v) => set("branch_id", v)} testid="rr-filter-branch" /></div>
            </label>)}
          {has("project_type") && (
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Project Type
              <select data-testid="rr-filter-projecttype" className={`${inputCls} block mt-1 max-w-[180px]`}
                value={filters.project_type || ""} onChange={(e) => set("project_type", e.target.value)}>
                <option value="">All types</option>
                {masters.workTypes.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </label>)}
          {has("fy") && (
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">FY
              <select data-testid="rr-filter-fy" className={`${inputCls} block mt-1`}
                value={filters.fy || ""} onChange={(e) => set("fy", e.target.value)}>
                <option value="">All FYs</option>
                {masters.fys.map((f) => <option key={f.id || f.code} value={f.code}>{f.code}</option>)}
              </select>
            </label>)}
          {has("mode") && (
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Mode
              <select data-testid="rr-filter-mode" className={`${inputCls} block mt-1`}
                value={filters.mode || ""} onChange={(e) => set("mode", e.target.value)}>
                <option value="">All modes</option>
                {MODES.map((m) => <option key={m}>{m}</option>)}
              </select>
            </label>)}
          {has("group_by") && (
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Group By
              <select data-testid="rr-filter-groupby" className={`${inputCls} block mt-1`}
                value={filters.group_by || ""} onChange={(e) => set("group_by", e.target.value)}>
                <option value="">No grouping</option>
                {["date", "client", "project", "branch", "mode", "voucher"].map((g) =>
                  <option key={g} value={g}>{g[0].toUpperCase() + g.slice(1)}-wise</option>)}
              </select>
            </label>)}
          {has("head") && (
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Retention Head
              <select data-testid="rr-filter-head" className={`${inputCls} block mt-1`}
                value={filters.head || ""} onChange={(e) => set("head", e.target.value)}>
                <option value="">All heads</option>
                {RETENTION_HEADS.map((h) => <option key={h}>{h}</option>)}
              </select>
            </label>)}
          {has("ret_status") && (
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Status
              <select data-testid="rr-filter-retstatus" className={`${inputCls} block mt-1`}
                value={filters.ret_status || ""} onChange={(e) => set("ret_status", e.target.value)}>
                <option value="">All</option>
                {["Held", "Partially Released", "Released"].map((s) => <option key={s}>{s}</option>)}
              </select>
            </label>)}
          {has("note_type") && (
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Note Type
              <select data-testid="rr-filter-notetype" className={`${inputCls} block mt-1`}
                value={filters.note_type || "all"} onChange={(e) => set("note_type", e.target.value)}>
                <option value="all">CN + DN</option><option value="CN">Credit Notes</option>
                <option value="DN">Debit Notes</option>
              </select>
            </label>)}
          {has("rv_status") && (
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Voucher Status
              <select data-testid="rr-filter-rvstatus" className={`${inputCls} block mt-1`}
                value={filters.rv_status || ""} onChange={(e) => set("rv_status", e.target.value)}>
                <option value="">All</option><option>Posted</option><option>Reversed</option>
              </select>
            </label>)}
          {has("queue_view") && (
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">View
              <select data-testid="rr-filter-view" className={`${inputCls} block mt-1`}
                value={filters.queue_view || "all"} onChange={(e) => set("queue_view", e.target.value)}>
                {["all", "outstanding", "fully_paid", "credit_note", "retention_pending"].map((v) =>
                  <option key={v} value={v}>{v.replace("_", " ")}</option>)}
              </select>
            </label>)}
          {has("ses_pending") && (
            <label className="flex items-center gap-2 text-xs text-slate-600 pb-2 cursor-pointer">
              <input type="checkbox" data-testid="rr-filter-sespending" checked={!!filters.ses_pending}
                onChange={(e) => set("ses_pending", e.target.checked)} /> SES Pending only
            </label>)}
          {has("only_variance") && (
            <label className="flex items-center gap-2 text-xs text-slate-600 pb-2">
              <input type="checkbox" data-testid="rr-filter-variance" checked={!!filters.only_variance}
                onChange={(e) => set("only_variance", e.target.checked)} /> Only variance rows
            </label>)}
          {has("q") && (
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Search
              <input data-testid="rr-filter-q" className={`${inputCls} block mt-1 w-48`} placeholder="Invoice / receipt / client…"
                value={filters.q || ""} onChange={(e) => set("q", e.target.value)} />
            </label>)}
          <button onClick={() => run()} disabled={loading} data-testid="rr-run-btn"
            className="inline-flex items-center gap-1.5 bg-[#F97316] hover:bg-[#EA6A0A] text-white px-4 py-2 text-xs font-bold uppercase tracking-wider disabled:opacity-50">
            {loading ? <Loader2 size={13} className="animate-spin" /> : <Play size={13} />} Run
          </button>
        </div>
      </Card>

      {data?.totals && (
        <div className="flex flex-wrap gap-2 mb-4" data-testid="rr-totals">
          {active.totals(data.totals).map(([k, v]) => (
            <div key={k} className="border border-slate-300 bg-white px-3 py-2">
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{k}</div>
              <div className="text-sm font-bold tabular-nums">{v}</div>
            </div>
          ))}
        </div>
      )}

      {data?.groups?.length > 0 && (
        <Card className="mb-4">
          <div className="px-4 py-2 border-b border-slate-100 text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">
            Grouped Subtotals ({filters.group_by}-wise)
          </div>
          <table className="w-full text-xs" data-testid="rr-groups-table">
            <thead><tr className="bg-slate-100 text-slate-600">
              {["Group", "Receipts", "Gross", "TDS", "Net"].map((h) => (
                <th key={h} className="px-3 py-2 text-left text-[10px] uppercase tracking-wider">{h}</th>))}
            </tr></thead>
            <tbody>{data.groups.map((g) => (
              <tr key={g.group} className="border-b border-slate-50">
                <td className="px-3 py-1.5 font-semibold">{g.group}</td>
                <td className="px-3 py-1.5 tabular-nums">{g.receipts}</td>
                <td className="px-3 py-1.5 tabular-nums">{INR(g.amount)}</td>
                <td className="px-3 py-1.5 tabular-nums">{INR(g.tds)}</td>
                <td className="px-3 py-1.5 tabular-nums font-bold">{INR(g.net)}</td>
              </tr>))}</tbody>
          </table>
        </Card>
      )}

      <ReportTable rep={active} rows={rows} loading={loading} expanded={expanded} setExpanded={setExpanded} />

      {active.drill && drillRows.length > 0 && (
        <div className="mt-6">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500 mb-2">{active.drill.label}</div>
          <ReportTable rep={{ ...active.drill, key: `${active.key}-drill` }} rows={drillRows} loading={false} />
        </div>
      )}
    </div>
  );
}

function ReportTable({ rep, rows, loading, expanded, setExpanded }) {
  // Jul-2026 — every report table gets an instant search + pagination.
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  useEffect(() => { setPage(1); setExpanded?.(null); /* eslint-disable-next-line */ }, [q, pageSize, rows]);

  const filtered = useMemo(() => {
    const n = q.trim().toLowerCase();
    if (!n) return rows;
    return rows.filter((r) => rep.columns.some((c) => String(r[c.k] ?? "").toLowerCase().includes(n)));
  }, [rows, q, rep]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  const cur = Math.min(page, pageCount);
  const start = (cur - 1) * pageSize;
  const paged = filtered.slice(start, start + pageSize);

  return (
    <Card>
      <div className="px-3 py-2 border-b border-slate-100 flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search this report…"
            data-testid={`rr-search-${rep.key}`}
            className={`${inputCls} pl-7 w-64`} />
        </div>
        <div className="text-[11px] text-slate-500 ml-auto" data-testid={`rr-count-${rep.key}`}>
          {filtered.length} row{filtered.length === 1 ? "" : "s"}
          {filtered.length !== rows.length ? ` (filtered from ${rows.length})` : ""}
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs" data-testid={`rr-table-${rep.key}`}>
          <thead>
            <tr className="bg-slate-900 text-white">
              {rep.expandable && <th className="w-8" />}
              {rep.columns.map((c) => (
                <th key={c.k} className={`px-3 py-2.5 text-[10px] uppercase tracking-wider whitespace-nowrap text-${c.align}`}>{c.h}</th>))}
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={rep.columns.length + 1} className="px-4 py-10 text-center text-slate-400">
              <Loader2 size={18} className="animate-spin inline mr-2" />Loading…</td></tr>}
            {!loading && !paged.length && <tr><td colSpan={rep.columns.length + 1}
              className="px-4 py-10 text-center text-slate-400">
              {rows.length && q ? "No rows match your search." : "No rows for the selected filters."}</td></tr>}
            {!loading && paged.map((r, idx) => {
              const i = start + idx;
              const hist = rep.expandable ? (r[rep.expandable] || []) : [];
              const isOpen = expanded === i;
              return (
                <FragmentRow key={i} r={r} i={i} rep={rep} hist={hist} isOpen={isOpen} setExpanded={setExpanded} />
              );
            })}
          </tbody>
        </table>
      </div>
      {filtered.length > 0 && (
        <div className="px-3 py-2 border-t border-slate-100 flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
          <span data-testid={`rr-pageinfo-${rep.key}`}>
            Showing {start + 1}–{Math.min(start + pageSize, filtered.length)} of {filtered.length}
          </span>
          <span className="ml-auto flex items-center gap-1.5">
            <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}
              data-testid={`rr-pagesize-${rep.key}`} className="border border-slate-300 px-1.5 py-1 text-[11px] bg-white">
              {[25, 50, 100, 200].map((n) => <option key={n} value={n}>{n} / page</option>)}
            </select>
            <button onClick={() => setPage(Math.max(1, cur - 1))} disabled={cur <= 1}
              data-testid={`rr-page-prev-${rep.key}`}
              className="border border-slate-300 p-1 disabled:opacity-40 hover:border-orange-400">
              <ChevronLeft size={13} />
            </button>
            <span className="tabular-nums">Page {cur} / {pageCount}</span>
            <button onClick={() => setPage(Math.min(pageCount, cur + 1))} disabled={cur >= pageCount}
              data-testid={`rr-page-next-${rep.key}`}
              className="border border-slate-300 p-1 disabled:opacity-40 hover:border-orange-400">
              <ChevronRight size={13} />
            </button>
          </span>
        </div>
      )}
    </Card>
  );
}

function FragmentRow({ r, i, rep, hist, isOpen, setExpanded }) {
  return (
    <>
      <tr className="border-b border-slate-50 hover:bg-orange-50/40">
        {rep.expandable && (
          <td className="px-2 py-1.5">
            {hist.length > 0 && (
              <button onClick={() => setExpanded(isOpen ? null : i)} data-testid={`rr-expand-${i}`}
                className="text-slate-400 hover:text-orange-600">
                {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              </button>)}
          </td>)}
        {rep.columns.map((c) => (
          <td key={c.k} className={`px-3 py-1.5 whitespace-nowrap tabular-nums text-${c.align} ${c.fmt === "inr" ? "font-medium" : ""}`}>
            {c.fmt === "inr" ? INR(r[c.k]) : (r[c.k] ?? "") === "" ? "—" : String(r[c.k])}
          </td>))}
      </tr>
      {isOpen && hist.length > 0 && (
        <tr className="bg-slate-50">
          <td />
          <td colSpan={rep.columns.length} className="px-4 py-2">
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Allocation History</div>
            <table className="text-xs w-full max-w-2xl" data-testid={`rr-history-${i}`}>
              <thead><tr className="text-slate-500">
                {["Invoice", "Amount", "Voucher", "Date", "Status"].map((h) =>
                  <th key={h} className="text-left px-2 py-1 text-[10px] uppercase">{h}</th>)}
              </tr></thead>
              <tbody>{hist.map((a, j) => (
                <tr key={j} className="border-t border-slate-200">
                  <td className="px-2 py-1">{a.invoice_number}</td>
                  <td className="px-2 py-1 tabular-nums">{INR(a.amount)}</td>
                  <td className="px-2 py-1">{a.voucher_number || "—"}</td>
                  <td className="px-2 py-1">{a.allocation_date}</td>
                  <td className="px-2 py-1">{a.status}</td>
                </tr>))}</tbody>
            </table>
          </td>
        </tr>
      )}
    </>
  );
}
